import { isDevelopment, isVercel } from 'utils/environment';
import { AllowedExperimentKey } from '@tenants/types';

/**
 * ExperimentLogger - Centralized logging for GrowthBook experiments
 *
 * Provides structured, color-coded logging for experiment evaluation flow.
 * Only logs in development and preview environments for debugging.
 *
 * ## Essential Logging
 * Logs three key pieces of information:
 * 1. GrowthBook SSR Payload - What GrowthBook decided during SSR
 * 2. Override Requested - Whether URL/header override was requested (boolean + value)
 * 3. Final Decision - The final decision after comparing SSR payload and overrides
 *
 * ## Color Coding
 * - Blue: SSR payload information
 * - Yellow: Override information
 * - Green: Final decision (variation)
 * - Red: Final decision (control)
 *
 * @example
 * ```typescript
 * ExperimentLogger.ssrPayload(experimentKey, variationId, inExperiment);
 * ExperimentLogger.override(experimentKey, source, variationId);
 * ExperimentLogger.finalDecision(experimentKey, showVariation, variationId);
 * ```
 */

const PREFIX = '[GrowthBook]';

// Color codes for different log types
const COLORS = {
  blue: 'color: #3b82f6; font-weight: bold',
  green: 'color: #10b981; font-weight: bold',
  yellow: 'color: #f59e0b; font-weight: bold',
  red: 'color: #ef4444; font-weight: bold',
  gray: 'color: #6b7280',
  purple: 'color: #8b5cf6; font-weight: bold',
  cyan: 'color: #06b6d4; font-weight: bold',
} as const;

/**
 * Check if logging should be enabled
 */
function shouldLog(): boolean {
  return isDevelopment || isVercel;
}

/**
 * Format a log message with color coding
 */
function log(context: string, message: string, color: keyof typeof COLORS, data?: Record<string, unknown>) {
  if (!shouldLog()) return;
  
  const fullMessage = `${PREFIX}:${context} ${message}`;
  
  if (data && Object.keys(data).length > 0) {
    console.debug(`%c${fullMessage}`, COLORS[color], data);
  } else {
    console.debug(`%c${fullMessage}`, COLORS[color]);
  }
}

/**
 * 1. Log GrowthBook SSR Payload
 * What GrowthBook decided during server-side rendering
 */
const ssrPayload = (
  experimentKey: AllowedExperimentKey,
  variationId: number,
  inExperiment: boolean,
  tenantId?: string
) => {
  log('SSR Payload', `Experiment: ${experimentKey}`, 'blue', {
    experimentKey,
    variationId,
    inExperiment,
    tenantId,
  });
};

/**
 * 2. Log Override Requested
 * Whether a URL or header override was requested
 */
const override = (
  experimentKey: AllowedExperimentKey,
  source: 'url' | 'header',
  variationId: number,
  tenantId?: string
) => {
  log('Override', `${source.toUpperCase()}: ${experimentKey}`, 'yellow', {
    experimentKey,
    source,
    variationId,
    requested: true,
    tenantId,
  });
};

/**
 * 3. Log Final Decision
 * The final decision after comparing SSR payload and overrides
 */
const finalDecision = (
  experimentKey: AllowedExperimentKey,
  showVariation: boolean,
  variationId: number,
  tenantId?: string,
  overrideApplied?: boolean
) => {
  const color = showVariation ? 'green' : 'red';
  const decision = showVariation ? 'VARIATION' : 'CONTROL';
  
  log('Final Decision', `${decision}: ${experimentKey}`, color, {
    experimentKey,
    showVariation,
    variationId,
    tenantId,
    overrideApplied,
  });
};

/**
 * Export the centralized experiment logger
 * 
 * Three essential logging functions:
 * 1. ssrPayload - What GrowthBook decided during SSR
 * 2. override - Whether URL/header override was requested
 * 3. finalDecision - The final decision after comparing SSR and overrides
 */
export const ExperimentLogger = {
  ssrPayload,
  override,
  finalDecision,
} as const;
